import { createContext, useContext, useEffect, useMemo } from 'react';

/**
 * ThemeProvider.jsx
 *
 * Purely generic: it accepts a `theme` object ({ primary, dark }) and a
 * `title` string and exposes them through context + CSS variables.
 * It has zero knowledge of which system produced this theme.
 */

const ThemeContext = createContext(null);

export function ThemeProvider({ theme, title, children }) {
  const value = useMemo(
    () => ({
      primary: theme?.primary ?? '#1976D2',
      dark: Boolean(theme?.dark),
      title: title ?? 'Application',
    }),
    [theme, title]
  );

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--erp-primary', value.primary);
    root.style.setProperty('--erp-bg', value.dark ? '#121212' : '#f4f6f8');
    root.style.setProperty('--erp-surface', value.dark ? '#1e1e1e' : '#ffffff');
    root.style.setProperty('--erp-text', value.dark ? '#f1f1f1' : '#1a1a1a');
    document.title = value.title;
  }, [value]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
